import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, IconButton, Typography, Icon, TextField, InputAdornment, List, ListItem, Collapse, ListItemText, ListItemIcon, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles, { listStyles } from './TaskTag.styles';
import { get, size, set } from 'global/lib/lodash';
import Form from 'global/components/form';
import { useScreen, Loader } from 'global/styles';
import { Alert } from '@mui/material';
import useRetrieveData from 'global/hooks/useRetrieveData';
import clsx from 'clsx';
import gstyles from 'global/styles';
import getHighlightedText from 'global/components/highlight-text';
import ButtonToggle from 'global/components/btn-toggle';
import useDebounce from 'global/hooks/useDebounce';
import { sortItemsAlphabetically } from 'global/utils';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from '../../admin/account-settings';

const scorecardsOpts = {
  build: ['scorecards'],
  useEffect: true,
  useCache: {
    reload: 20,
  },
};

const TagButton = (props) => {
  const { isActive } = props;
  const styles = listStyles();

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.onClick && props.onClick();
  };

  return (
    <Button onClick={onClick} className={clsx(styles.tagContainer, isActive && styles.tagActive)}>
      <Icon>label</Icon>
    </Button>
  );
};

const System = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(false);

  const { system } = props;
  const title = get(system, 'name', '');
  const checklists = get(system, 'checklists', []);

  useEffect(() => {
    const match = get(props, 'matches.term');
    if (match) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [props.matches]);

  return (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} primaryTypographyProps={{ variant: 'button' }} />
        <ListItemIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      <Collapse in={open} unmountOnExit>
        <List disablePadding className={styles.marginLeft}>
          {checklists.map((check, i) => (
            <Checklist {...props} key={get(check, 'checklistId')} checklist={check} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const Checklist = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(false);

  const { checklist } = props;
  const title = get(checklist, 'name', '');

  const checkpoints = get(checklist, 'checkpoints', []);

  useEffect(() => {
    const match = get(props, 'matches.term');
    if (match) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [props.matches]);

  const tagMe = useCallback((checkpoint) => {
    props.changeTag({
      ...checkpoint,
      checkpointId: get(checkpoint, 'id'),
      checklistId: get(checklist, 'id'),
      score: get(checkpoint, 'selectedOption.value'),
      criteria: [get(checkpoint, 'description'), get(checkpoint, 'selectedOption.criteria')].filter(Boolean).join(' '),
      scorecardConfig: get(checkpoint, 'selectedOption.scorecardConfig'),
      pssvChecklist: { ...checklist },
      pssvCheckpoint: { ...checkpoint },
    });
  }, []);

  return (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} />
        <ListItemIcon className={styles.marginRight}>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
        <TagButton onClick={() => tagMe()} />
      </ListItem>
      {!props.thinking && (
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {sortItemsAlphabetically(checkpoints).map((check) => (
              <Checkpoint key={get(check, 'checkpointId')} matches={props.matches} checkpoint={check} onClick={tagMe} {...props} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Checkpoint = (props) => {
  const styles = listStyles();

  const { checkpoint, matches } = props;
  const title = get(checkpoint, 'name', '');
  const description = get(checkpoint, 'description');
  // const options = get(checkpoint, 'checkpointOptions', []).map(o => ({ ...o, text: getHighlightedText(get(o, 'name'), matches.term) })).reverse();

  var variant = size(get(checkpoint, 'checkpointOptions', [])) > 2 ? 'normal' : 'passFail';
  var options = get(checkpoint, 'checkpointOptions', [])
    .sort((a, b) => a.value - b.value)
    .map((o, i) => ({
      criteria: get(o, 'name'),
      index: i,
      value: get(o, 'value', null),
    }));

  const selectOption = (vals) => {
    props.onClick({
      ...checkpoint,
      selectedOption: { ...vals, scorecardConfig: get(props, 'scorecardConfig', {}) },
    });
  };

  return (
    <ListItem>
      <ListItemText
        className={styles.checkpointText}
        primary={getHighlightedText(`${title}: ${description || ''}`, matches.term)}
        secondary={
          <ButtonToggle
            variant={variant}
            options={options}
            selected={undefined}
            onSelect={selectOption}
            getHighlight={(x) => getHighlightedText(x, matches.term)}
            scorecardConfig={get(props, 'scorecardConfig', {})}
          />
        }
      />
    </ListItem>
  );
};

export const TaskTag = (props) => {
  const [formLoading] = useState(false);
  const [error, setError] = useState();
  const [tags, setTags] = useState({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const [scorecardId, setScorecardId] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [textMatches, setTextMatches] = useState({
    systems: [],
    term: '',
  });
  const [systems, setSystems] = useState([]);

  const classes = useStyles();
  const styles = gstyles();
  const { isMobileXS } = useScreen();

  const handleClose = () => {
    props.onClose();
  };

  // const formSubmit = (vals) => {
  //   console.log(vals);
  // };

  const defaultScorecard = getAccountSetting(SETTINGS.assessmentScorecard);

  const scorecards = useRetrieveData({ url: `/scorecards` }, scorecardsOpts);
  const scData = useRetrieveData(
    { url: `/scorecards/${scorecardId}` },
    {
      waitFor: scorecardId,
      build: ['scorecards'],
      params: {
        include: 'realm,checklists,checklists.checkpoints,checklists.checkpoints.checkpointOptions,checklists.classification,checklists.classification.system',
      },
    }
  );

  const formUpdate = (vals) => {
    setScorecardId(vals.scorecard);
  };

  const reset = () => {
    setTags({});
    setTextMatches({
      systems: [],
      checklists: [],
      term: '',
    });
    setSearch('');
    setThinking(false);
  };

  const changeTag = useCallback((tag) => {
    setTags(tag);
  }, []);

  const searchChange = (val) => {
    setSearch(val);
    setThinking(true);
  };

  useEffect(() => {
    if (Object.keys(tags).length > 0) {
      props.onTagSelected({
        ...tags,
        scorecardId,
      });
      props.onClose();
      reset();
    }
  }, [tags]);

  useEffect(() => {
    const sys = {
      // [id]: {
      //   ...system,
      //   checklists: [...checklists],
      // }
    };
    if (scData.data) {
      const clists = get(scData, 'data.0.checklists', []);
      clists.forEach((clist) => {
        const syst = get(clist, 'classification.system', {});
        const listCheck = get(sys, [syst.id, 'checklists'], []);
        set(sys, syst.id, syst);
        set(sys, [syst.id, 'checklists'], [...listCheck, clist]);
      });
      const newSystems = [];
      for (let k in sys) {
        newSystems.push({ ...sys[k] });
      }
      setSystems(newSystems);
    }
  }, [scData.data]);

  useEffect(() => {
    const term = (search || '').toLowerCase();
    if (term) {
      const newSystems = systems
        .map((sys) => {
          const checklists = get(sys, 'checklists', []);

          const newChecklists = checklists
            .map((cl) => {
              const cps = get(cl, 'checkpoints', []);

              const newCheckpoints = cps
                .map((cp) => {
                  const desc = (get(cp, 'description', '') || '').toLowerCase();
                  const chpOptDes = get(cp, 'checkpointOptions', [])
                    .map((cpo) => get(cpo, 'name', ''))
                    .join(' ');

                  if (desc.indexOf(term) > -1 || chpOptDes.indexOf(term) > -1) {
                    return cp;
                  } else {
                    return null;
                  }
                })
                .filter((cp) => cp);

              return size(newCheckpoints) ? { ...cl, checkpoints: newCheckpoints } : null;
            })
            .filter((cl) => cl);

          return size(newChecklists) ? { ...sys, checklists: newChecklists } : null;
        })
        .filter((sys) => sys);

      setTextMatches({
        systems: newSystems,
        term,
      });
    } else {
      setTextMatches({ systems: [], term: '' });
    }
    setTimeout(() => {
      setThinking(false);
    }, 200);
  }, [debouncedSearch]);

  const showBlank = size(textMatches.term) > 0 && !size(textMatches.systems);

  return (
    <Dialog className={classes.dialog} disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" className={classes.subtitle}>
          LINK TO LIBRARY
        </Typography>

        <div className={classes.inputContainer}>
          <Form
            loading={formLoading}
            showAllScreens={true}
            formStyle={classes.formStyle}
            // onSubmit={formSubmit}
            onFormState={formUpdate}
            screens={[
              {
                title: '',
                titleStyle: classes.titleStyle,
                screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                screenContainer: {
                  props: {
                    className: classes.screenWidth,
                  },
                },
                inputs: [
                  {
                    id: 'scorecard',
                    type: 'Select',
                    data: () => (scorecards.data || []).map((d) => ({ title: d.name, value: d.id })),
                    defaultValue: () => scorecardId || defaultScorecard,
                    props: {
                      label: scorecards.loading ? 'Loading...' : 'Scorecard',
                    },
                  },
                ],
              },
            ]}
          />

          <div className={classes.searchContainer}>
            <TextField
              variant="outlined"
              fullWidth
              id="search"
              value={search}
              label="Search"
              onChange={(e) => searchChange(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">{thinking ? <Loader size={20} /> : <Icon>search</Icon>}</InputAdornment>,
              }}
            />
          </div>

          <div className={clsx(classes.inputContainer, classes.list)}>
            {!scorecards.loading && !!scData.loading && !!scorecardId && (
              <div className={styles.centerColumn}>
                <Loader />
              </div>
            )}

            {!!showBlank && (
              <div className={styles.centerColumn} style={{ padding: 20 }}>
                <span>{`There are no results that meet your search criteria.`}</span>
              </div>
            )}

            {!scData.loading && !!size(systems) && !showBlank && (
              <List component="nav">
                {(size(textMatches.systems) ? textMatches.systems : systems).map((sys, i) => (
                  <System key={i} changeTag={changeTag} system={sys} matches={textMatches} thinking={thinking} scorecardConfig={get(scData, 'data.0.config', {})} />
                ))}
              </List>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TaskTag;
